import React, { useEffect } from "react";
import classes from "./MisaHeaderSection.module.scss";
import { UserPointOwner } from "../../api/UserPointOwnerApi";
import {
  fetchUserInvestmentHeaderMessage,
  UserInvestmentHeaderMessage,
  UserInvestmentHeaderMessageSegment,
} from "../../api/UserInvestmentHeaderMessageApi";
import TeacherComment from "../../molecules/teacher-commnet/TeacherComment";
import rabbitWinkNormal from "../../static/images/rabbit-wink-normal.png";
import commentChar from "../../static/images/point/comment-char.png";
import misaHeaderBanner from "../../static/images/point/misa-header-banner.png";
import pointCommentPattern1 from "../../static/images/point/point-comment-pattern1.png";
import pointCommentPattern2 from "../../static/images/point/point-comment-pattern2.png";
import pointCommentPattern3 from "../../static/images/point/point-comment-pattern3.png";
import pointCommentPattern4 from "../../static/images/point/point-comment-pattern4.png";
import pointCommentPattern5 from "../../static/images/point/point-comment-pattern5.png";
import childChar1 from "../../static/images/character/child-char-1.png";
import childChar2 from "../../static/images/character/child-char-2.png";
import childChar3 from "../../static/images/character/child-char-3.png";
import childChar4 from "../../static/images/character/child-char-4.png";
import childChar5 from "../../static/images/character/child-char-5.png";
import childChar6 from "../../static/images/character/child-char-6.png";
import childChar7 from "../../static/images/character/child-char-7.png";

type MisaHeaderSectionProps = {
  pointOwners: UserPointOwner[];
  selectedPointOwnerId: number | null;
  onSelectPointOwner: (pointOwnerId: number) => void;
  onAddChildClick: () => void;
};

// 子どものアイコン種別から表示画像を引くためのマップ。
const childIcons: Record<string, string> = {
  "child-char-1": childChar1,
  "child-char-2": childChar2,
  "child-char-3": childChar3,
  "child-char-4": childChar4,
  "child-char-5": childChar5,
  "child-char-6": childChar6,
  "child-char-7": childChar7,
};

// ヘッダーコメント API の画像名から、バンドル済み画像を引くためのマップ。
const headerCommentImages: Record<string, string> = {
  "point-comment-pattern1.png": pointCommentPattern1,
  "point-comment-pattern2.png": pointCommentPattern2,
  "point-comment-pattern3.png": pointCommentPattern3,
  "point-comment-pattern4.png": pointCommentPattern4,
  "point-comment-pattern5.png": pointCommentPattern5,
};

// メッセージの強調指定に応じて、文字色や太字の className を返す。
const getSegmentClassName = (segment: UserInvestmentHeaderMessageSegment) => {
  if (!segment.emphasis) {
    return "";
  }
  if (segment.emphasisColor === "orange") {
    return classes.orangeEmphasis;
  }
  if (segment.emphasisColor === "green") {
    return classes.greenEmphasis;
  }
  return classes.emphasis;
};

// 保有主体の種類に応じて、本人または子どもの表示アイコンを返す。
const getOwnerImage = (owner: UserPointOwner) => {
  if (owner.ownerType === "user") {
    return rabbitWinkNormal;
  }
  return childIcons[owner.iconType ?? ""] ?? childChar1;
};

// 保有主体カードに表示する名前。本人は固定表示、子どもは登録名を使う。
const getOwnerLabel = (owner: UserPointOwner) => {
  if (owner.ownerType === "user") {
    return "あなた";
  }
  return owner.ownerName || "こども";
};

const MisaHeaderSection = ({
  pointOwners,
  selectedPointOwnerId,
  onSelectPointOwner,
  onAddChildClick,
}: MisaHeaderSectionProps) => {
  // 選択中の保有主体に合わせて、先生コメントの文言と画像を取得する。
  const headerMessageApi = fetchUserInvestmentHeaderMessage();
  const headerMessage = headerMessageApi.state?.data as unknown as
    | UserInvestmentHeaderMessage
    | null;

  // 子どもは最大4人まで追加できるため、上限に達したら追加ボタンを無効化する。
  const isAddChildDisabled = pointOwners.filter(
    (owner) => owner.ownerType === "child"
  ).length >= 4;

  // 選択中の保有主体が子どもかどうかで、ヘッダー背景などの表示を切り替える。
  const selectedPointOwner = pointOwners.find(
    (owner) => owner.pointOwnerId === selectedPointOwnerId
  );
  const isChildSelected = selectedPointOwner?.ownerType === "child";

  useEffect(() => {
    // 保有主体が選択されたら、その主体向けのヘッダーコメントを取得する。
    if (selectedPointOwnerId === null) {
      return;
    }

    headerMessageApi.fetch({ pointOwnerId: selectedPointOwnerId });
  }, [selectedPointOwnerId, pointOwners]);

  return (
    <section
      className={`${classes.heroSection} ${
        isChildSelected ? classes.childSelectedHeroSection : ""
      }`}
    >
      {/* MISA のメインビジュアルを表示する。 */}
      <img
        className={classes.misaHeaderBanner}
        src={misaHeaderBanner}
        alt="気になる業界を推しを見つけて応援しよう"
      />

      {/* API から取得した先生コメントを、強調表現つきで表示する。 */}
      {headerMessage && (
        <div className={classes.headerComment}>
          <TeacherComment
            image={
              headerCommentImages[headerMessage.characterImageName] ?? commentChar
            }
            charRight={headerMessage.characterPosition === "right"}
          >
            <span className={classes.headerCommentText}>
              {(headerMessage.messageSegments?.length
                ? headerMessage.messageSegments
                : [
                    {
                      text: headerMessage.message,
                      emphasis: false,
                      emphasisColor: null,
                    },
                  ]
              ).map((segment, index) => (
                <span
                  key={`misa-header-message-segment-${index}`}
                  className={getSegmentClassName(segment)}
                >
                  {segment.text}
                </span>
              ))}
            </span>
          </TeacherComment>
        </div>
      )}

      {/* 子どもの追加ボタンと、ポイント保有主体の切り替えカードを表示する。 */}
      <div className={classes.headerContent}>
        {!isChildSelected && (
          <button
            type="button"
            className={`${classes.addChildButton} ${
              isAddChildDisabled ? classes.disabledAddChildButton : ""
            }`}
            onClick={onAddChildClick}
            disabled={isAddChildDisabled}
          >
            <span className={classes.plusIcon}>+</span>
            お子さまを追加する
          </button>
        )}

        <div className={classes.profileRow}>
          {pointOwners.map((owner) => {
            // 各カードの表示名と選択状態を算出して、選択時の見た目を切り替える。
            const label = getOwnerLabel(owner);
            const isSelected = selectedPointOwnerId === owner.pointOwnerId;
            
            return (
              <button
                key={owner.pointOwnerId}
                type="button"
                className={`${classes.ownerCard} ${isSelected ? classes.selected : ""}`}
                onClick={() => onSelectPointOwner(owner.pointOwnerId)}
              >
                <img src={getOwnerImage(owner)} alt={label} />
                <span>{label}</span>
                {isSelected && <div className={classes.ownerPointer} />}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default MisaHeaderSection;
